// =========================================
// API Response Types for /v1/reports:generateMatrix
// =========================================

/**
 * Response from /v1/reports:generateMatrix endpoint
 */
export interface GenerateMatrixResponse {
  dateRange: DateRange;
  groupingConfig: ReportGroupingConfig;
  columns: DateColumn[];
  rows: MatrixRow[];
  grandTotal: {
    totalSeconds: number;
    dailyCells: MatrixCell[];
  };
  metadata: {
    availableUsers: Array<{
      id: string;
      displayName: string;
    }>;
    availableProjects: Array<{
      id: string;
      key: string;
      name: string;
    }>;
    isAdmin: boolean;
  };
}

// =========================================
// Grouping Configuration Types
// =========================================

/**
 * Available grouping levels for report data
 */
export type GroupingLevel = 'project' | 'user' | 'issue' | 'label';

/**
 * Configuration for a single grouping level
 */
export interface GroupingConfig {
  level: GroupingLevel;
  enabled: boolean;
  order: number;
}

/**
 * Full report grouping configuration
 */
export interface ReportGroupingConfig {
  levels: GroupingConfig[];
}

// =========================================
// Report Data Types
// =========================================

/**
 * Data for a single cell in the matrix
 */
export interface MatrixCell {
  date: string;
  totalSeconds: number;
  isWeekend: boolean;
  logCount: number;
}

/**
 * Data for a matrix row - represents one level of hierarchy
 */
export interface MatrixRow {
  id: string;
  level: GroupingLevel;
  label: string;
  entityId: string;
  entityKey?: string;
  labels?: string[];
  cells: MatrixCell[];
  totalSeconds: number;
  children?: MatrixRow[];
  isExpanded?: boolean;
  depth: number;
}

/**
 * Complete report data
 */
export interface ReportData {
  dateRange: DateRange;
  groupingConfig: ReportGroupingConfig;
  rows: MatrixRow[];
  columnDates: DateColumn[];
  grandTotal: {
    totalSeconds: number;
    dailyTotals: MatrixCell[];
  };
}

/**
 * Date column in header
 */
export interface DateColumn {
  date: string;
  dayOfWeek: number;
  dayName: string;
  dayNumber: number;
  isWeekend: boolean;
  isToday: boolean;
}

/**
 * Report date range
 */
export interface DateRange {
  startDate: string;
  endDate: string;
}

// =========================================
// Source Data Types (API)
// =========================================

/**
 * Raw worklog data (extended with relations)
 */
export interface ReportWorkLogEntry {
  id: string;
  issueId: string;
  issueKey: string;
  issueSummary: string;
  projectId: string;
  projectName: string;
  authorId: string;
  authorDisplayName: string;
  startedTime: string;
  timeSpentSeconds: number;
  date: string;
}

/**
 * Project
 */
export interface Project {
  id: string;
  name: string;
  key: string;
  displayName?: string;
  createTime?: string;
  updateTime?: string;
}

export interface ProjectsListResponse {
  items: Project[];
  nextPageToken?: string | null;
  totalSize?: number | null;
}

// =========================================
// UI State Types
// =========================================

/**
 * View type for report granularity
 */
export type ReportViewType = 'daily' | 'weekly' | 'monthly';

/**
 * Matrix row expansion state
 */
export interface ExpandedRowsState {
  [rowId: string]: boolean;
}

/**
 * Report filter state
 */
export interface ReportFilters {
  dateRange: DateRange;
  groupingConfig: ReportGroupingConfig;
  viewType: ReportViewType;
  selectedUsers?: string[];
  selectedProjects?: string[];
  selectedLabels?: string[];
}

// =========================================
// Predefined Configurations
// =========================================

export const MAX_ENABLED_GROUPING_LEVELS = 3;

export const DEFAULT_GROUPING_CONFIG: ReportGroupingConfig = {
  levels: [
    { level: 'project', enabled: false, order: 0 },
    { level: 'label', enabled: false, order: 1 },
    { level: 'user', enabled: true, order: 2 },
    { level: 'issue', enabled: true, order: 3 },
  ],
};

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getDefaultDateRange = (): DateRange => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  // Last day of current month (day 0 of next month = last day of current month)
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  return {
    startDate: formatLocalDate(start),
    endDate: formatLocalDate(end),
  };
};

export const GROUPING_LEVEL_LABELS: Record<GroupingLevel, string> = {
  project: 'Project',
  user: 'User',
  issue: 'Issue',
  label: 'Label',
};

export const DEFAULT_VIEW_TYPE: ReportViewType = 'daily';

export const VIEW_TYPE_LABELS: Record<ReportViewType, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};
